import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSelector } from "react-redux";
import useTranslation from "../hooks/useTranslation";

const PaymentAmountCard = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const paymentData = useSelector((state) => state.payment.data);
  const inquiryHistory = useSelector((state) => state.inquiry.paymentLinkHistory);
  const { secondary_text_color } = useSelector((state) => state.style);

  const formatRupiah = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "-";
    }

    return `Rp ${numericValue.toLocaleString("id-ID")}`;
  };

  const baseAmount = paymentData?.total_amount ?? 0;
  const serviceFee = inquiryHistory?.merchant_fee ?? null;
  const customerPaysFee = Boolean(paymentData?.customer_pays_fee);
  const normalizedItems = (() => {
    if (Array.isArray(paymentData?.items)) {
      return paymentData.items;
    }

    if (typeof paymentData?.items === "string") {
      try {
        const parsedItems = JSON.parse(paymentData.items);
        return Array.isArray(parsedItems) ? parsedItems : [];
      } catch {
        return [];
      }
    }

    return [];
  })();
  const hasCustomerDetails = paymentData?.customer_email || paymentData?.customer_name || paymentData?.customer_phone;
  const hasItems = normalizedItems.length > 0;
  const amountToPay =
    customerPaysFee && serviceFee !== null && serviceFee !== undefined
      ? Number(baseAmount) + Number(serviceFee)
      : baseAmount;
  const displayDescription =
    paymentData?.source === "api v1" ? paymentData?.title || "-" : paymentData?.description || "-";
  const displayCustomerDetails = [
    { label: t("common.name"), value: paymentData?.customer_name },
    { label: t("common.phoneNumber"), value: paymentData?.customer_phone },
    { label: t("common.email"), value: paymentData?.customer_email },
  ];
  const getItemName = (item) => item.name || item.item_name || "-";
  const getItemQuantity = (item) => Number(item.quantity || item.qty || 0);
  const getItemUnitPrice = (item) => Number(item.unit_price || item.price || item.amount || 0);
  const getItemSubtotal = (item) => {
    const explicitSubtotal = Number(item.subtotal || item.total || 0);

    if (explicitSubtotal > 0) {
      return explicitSubtotal;
    }

    return getItemQuantity(item) * getItemUnitPrice(item);
  };

  return (
    <div className="flex flex-col">
      <section className="overflow-hidden rounded-t-2xl bg-white shadow-sm">
        <div className="px-6 py-5">
          <p className="text-base font-normal text-gray-900" style={{ color: secondary_text_color }}>
            {t("paymentAmountCard.amountToPay")}
          </p>
          <p className="mt-2 text-2xl font-semibold text-gray-900" style={{ color: secondary_text_color }}>
            {formatRupiah(amountToPay)}
          </p>
        </div>
      </section>

      <section className="border-t rounded-b-2xl border-gray-200 px-6 py-4 shadow-sm" style={{ background: "#F9FAFA" }}>
        <button
          type="button"
          onClick={() => setIsOpen((value) => !value)}
          className="flex w-full items-center justify-between text-left text-base font-medium text-singaHijauPrimary"
        >
          <span className="text-base font-semibold">{t("paymentAmountCard.details")}</span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {isOpen && (
          <div className="mt-4 space-y-3 text-base">
            {hasCustomerDetails && (
              <>
                <div className="flex flex-col gap-2">
                  <span className="font-medium text-singaHijauPrimary">
                    {t("paymentAmountCard.customerInformation")}
                  </span>
                  {displayCustomerDetails.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="font-normal text-gray-400">{item.label}</span>
                      <span className="font-medium text-gray-900" key={index}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-dashed border-gray-400" />
              </>
            )}

            <div className="flex flex-col gap-2">
              <span className="text-[#9EA1A8]">{t("paymentAmountCard.description")}</span>
              <span className="max-w-[55%] font-medium text-gray-900 break-all">{displayDescription}</span>
            </div>

            {hasItems && (
              <>
                <div className="border-t border-dashed border-gray-400" />
                <div className="space-y-3">
                  {normalizedItems.map((item, index) => (
                    <div key={`${getItemName(item)}-${index}`} className="flex items-start justify-between gap-4">
                      <span className="min-w-0 flex-1 font-normal text-gray-400 break-words">{getItemName(item)}</span>
                      <span className="shrink-0 text-right font-normal text-gray-600">
                        {getItemQuantity(item)} x {formatRupiah(getItemUnitPrice(item))}
                        <span className="mx-2 text-gray-900">&bull;</span>
                        <strong className="font-medium text-gray-900">{formatRupiah(getItemSubtotal(item))}</strong>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-dashed border-gray-400" />
              </>
            )}

            {hasItems && (
              <div className="flex items-start justify-between gap-4">
                <span className="text-[#9EA1A8]">{t("paymentAmountCard.subTotal")}</span>
                <span className="text-right font-medium text-gray-900">{formatRupiah(amountToPay)}</span>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default PaymentAmountCard;
